import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Form, Input, Row, Col, Alert, Modal, Checkbox } from "antd";
import { RightOutlined, FileTextOutlined, CopyOutlined } from "@ant-design/icons";
import { MobileGeoLocationSelector } from "./CountrySelector";
import useMediaQuery from "use-media-antd-query";
import Coupon from "../../Shared/Coupon";
import { saveOnLocalStorage } from "../../../utils/helper";
import { BuyerInvoiceInfo, IsNullUndefinedEmpty } from "utils/helper";
import emailSpellChecker from "@zootools/email-spell-checker";
import { POPULAR_DOMAINS, POPULAR_TLDS } from "utils/emails";
import { vatid_get } from "../../../api/validations";
import { useInView } from "react-intersection-observer";
import CustomButton from "Components/CustomButton";
import { PhoneInput, defaultCountries, parseCountry } from "react-international-phone";
import phoneCountriesAllowed from "../../../config/phone_countries.json";
import { validatePhoneNumber } from "../../../utils/validations";
import { BillingName, BillingEmail, VatId, Address, City, PostalCode } from "../../Shared/Billing";
import { isNil, isNilOrEmpty } from "@salespark/toolkit";
import GiftSection from "./GiftSection";
import moment from "moment-timezone";
import "react-international-phone/style.css";

const { confirm } = Modal;

const Identification = ({ data, actionHandler, customStyle }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { country, region } = data?.geo;

  const { countries } = data;
  const isWidget = window?.location?.hash?.substr(1) === "widget";
  const colSize = useMediaQuery();
  const mobileSizes = ["xs", "sm", "md"];
  const isMobileSize = mobileSizes.includes(colSize);
  const [validationTrigger, setValidationTrigger] = useState("onBlur");
  const [inputEmail, setInputEmail] = React.useState("");
  const [errorLoadingVatIdData, setErrorLoadingVatIdData] = useState(false);
  const [emailSpellSuggestion, setEmailSpellSuggestion] = useState(null);
  const [phoneNumberData, setPhoneNumberData] = useState(null);
  const [isOfferPurchase, setIsOfferPurchase] = useState();
  const [loadingNextButton, setLoadingNextButton] = useState(false);
  const customFieldSize = data?.customization?.fields_size || "normal";
  const isSmallCustomFieldSize = customFieldSize === "small";

  let updateFieldsTimeout = null;
  // Use `inView` (viewport intersection only) and NOT `trackVisibility`/`isVisible`,
  // which is occlusion-aware and falsely fires when a modal mask covers the button.
  const { ref: nextButtonRef, inView: nextButtonInView } = useInView({
    threshold: 1,
  });

  useEffect(() => {
    form.resetFields();
    if (data?.identification?.free_resolution_checkbox === true) {
      delete data.identification?.free_resolution_checkbox;
    }

    // Check if theres field gift_delivery_date in data.identification
    // and set it to moment object
    if (data?.identification?.gift_delivery_date) {
      data.identification.gift_delivery_date = moment(data.identification.gift_delivery_date);
    }

    form.setFieldsValue(data.identification);

    setLoadingNextButton(true);
    setInputEmail(data.identification?.email);
    setIsOfferPurchase(data?.identification?.is_offer === true ? true : false);

    if (IsNullUndefinedEmpty(data.identification?.phone) === false) {
      setPhoneNumberData({ phone: data.identification?.phone });
    }

    if (
      //data.ask_vatid_on_checkout === true &&
      data.trigger_coupon === true &&
      data.temp_coupon !== undefined &&
      data.coupon.coupon_code === undefined
    ) {
      triggerCoupon();
    }

    form.setFieldsValue({ is_offer: data?.identification?.is_offer === true ? true : false });

    // Check if theres a next_step in data, and if theres name and email in the form
    // if so, submit no next step automatically
    if (data.next_step === "select-payment-method" && data.identification?.name && data.identification?.email) {
      form.submit();
    }
  }, []);

  useEffect(() => {
    emailSpellCheckerInfo(inputEmail);
    setLoadingNextButton(false);
  }, [inputEmail]);

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const allowedCountriesList = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return phoneCountriesAllowed.includes(iso2);
  });

  const triggerCoupon = () => {
    setTimeout(() => {
      actionHandler("coupon", data.temp_coupon);
    }, 500);
  };

  const validateVatId = async (vatid) => {
    return new Promise((resolve) => {
      vatid_get(vatid)
        .then((res) => {
          resolve({ status: true, data: res.data });
          setErrorLoadingVatIdData(false);
        })
        .catch((err) => {
          resolve({ status: false, data: err });
          setErrorLoadingVatIdData(true);
        });
    });
  };

  const onUpdateFields = () => {
    try {
      clearTimeout(updateFieldsTimeout);

      updateFieldsTimeout = setTimeout(() => {
        const data = form.getFieldsValue();
        data.email = inputEmail;
        data.phone = phoneNumberData?.phone;

        if (IsNullUndefinedEmpty(data)) return;

        if (IsNullUndefinedEmpty(data.name)) return;
        if (IsNullUndefinedEmpty(data.email)) return;

        actionHandler("update-identification", data);
      }, 1000);

      // Error handling
    } catch (error) {
      // console.log(error);
    }
  };

  const awaitUntilValuesAreSet = (values) => {
    return new Promise(async (resolve) => {
      let attemptsToSetEmail = 0;
      let attemptsToSetPhone = 0;
      if (values.email !== inputEmail) {
        setInputEmail(values.email);
        setLoadingNextButton(true);
      }

      do {
        await delay(100);
        attemptsToSetEmail++;
      } while (values.email !== inputEmail && attemptsToSetEmail < 25);

      if (values.email !== inputEmail) {
        Modal.error({
          title: t("identification.errorTitle"),
          content: t("identification.emailUpdateError"),
          okText: t("identification.ok"),
        });
        resolve(false);
        return;
      }

      if (values.phone !== phoneNumberData?.phone) {
        setPhoneNumberData({ phone: values.phone });
        setLoadingNextButton(true);
      }

      do {
        await delay(100);
        attemptsToSetPhone++;
      } while (values.phone !== phoneNumberData?.phone && attemptsToSetPhone < 25);

      if (values.phone !== phoneNumberData?.phone) {
        Modal.error({
          title: t("identification.errorTitle"),
          content: t("identification.phoneUpdateError"),
          okText: t("identification.ok"),
        });
        resolve(false);
        return;
      }

      resolve(true);
    });
  };
  const onFinish = async (values, overrideNotVatId = false) => {
    try {
      // Rules for when format is one-time-service
      if (data?.format === "one-time-service") {
        if (isNilOrEmpty(values?.vatid) && overrideNotVatId === false) {
          let confirmWithoutVatId = false;
          const modal = confirm({
            width: 600,
            title: t("identification.vatModalTitle"),
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>
                  {t("identification.vatNoVatId")}
                  <br /> <br />
                  {t("identification.vatNoVatIdQuestion")}
                  <br />
                </div>
                <Checkbox
                  onChange={(event) => {
                    confirmWithoutVatId = event.target.checked;
                    modal.update({ okButtonProps: { disabled: !confirmWithoutVatId } });
                  }}
                >
                  <strong>{t("identification.vatConfirmCheckbox")}</strong>
                </Checkbox>
                <br />
                <br />
                <Alert
                  message={
                    <em>
                      <small>{t("identification.vatCantFix")}</small>
                    </em>
                  }
                  type="warning"
                  showIcon
                />
              </div>
            ),
            okText: t("identification.yes"),
            okType: "primary",
            okButtonProps: { disabled: true },
            cancelText: t("identification.no"),
            keyboard: true,
            maskClosable: true,
            onOk() {
              onFinish(values, true);
            },
            onCancel() {
              // Focus vatid input
              form.getFieldInstance("vatid").focus();
              return;
            },
          });
          return;
        }
      }

      // Check if is offer checkbox is checked
      if (values?.is_offer === true) {
        // if if gift email is set, and gift_email_confirm is not set or not equal to gift_email, show error
        if (values?.gift_email) {
          if (!values?.gift_email_confirm || values?.gift_email !== values?.gift_email_confirm) {
            form.setFields([{ name: "gift_email_confirm", errors: [t("identification.offerEmailMismatch")] }]);
            return;
          }

          if (values?.email && values?.gift_email && values?.email.toLowerCase() === values?.gift_email.toLowerCase()) {
            form.setFields([{ name: "gift_email", errors: [t("identification.offerEmailSameAsOwn")] }]);
            Modal.error({
              title: t("identification.errorTitle"),
              content: t("identification.offerEmailSameAsOwn"),
              okText: t("identification.ok"),
            });
            return;
          }
        }
      }

      if (data?.free_resolution_waiver_enabled === true && data?.free_resolution_waiver_mandatory === true) {
        const free_resolution_checkbox = form.getFieldValue("free_resolution_checkbox");
        if (free_resolution_checkbox !== true) {
          Modal.error({
            title: t("identification.errorTitle"),
            content: t("identification.freeResolutionWarning"),
            okText: t("identification.ok"),
          });
          return;
        }
      }

      if (values?.email) {
        values.email = values.email.replace(/[^\x20-\x7E]/g, "").trim();
      }

      const valuesSetResponse = await awaitUntilValuesAreSet(values);
      if (valuesSetResponse === false) return;

      // Ensure that values include name and email
      if (IsNullUndefinedEmpty(values)) return;
      if (IsNullUndefinedEmpty(values.name)) return;
      if (IsNullUndefinedEmpty(values.email)) return;

      actionHandler("update-identification", values);

      if (errorLoadingVatIdData && !IsNullUndefinedEmpty(values?.vatid)) {
        Modal.error({ title: t("identification.vatInvalidTitle") });
        return;
      }

      if (values?.email !== undefined && values?.email !== null && values?.email !== "") {
        values.email = values.email.toLowerCase();
        values.email = values.email.replace(/\s/g, "");
      }

      if (IsNullUndefinedEmpty(values.phone)) {
        values.phone = "";
      }
      values.country = country.value;
      values.region = region.value;
      //values.tax_percent = selectedRegion.tax_percent;

      // Validate phone number and get validation object (isValid, title, message)
      const phoneValidation = validatePhoneNumber(phoneNumberData.phone, phoneNumberData, setPhoneNumberData, data?.customization?.phone_required === true);

      if (phoneValidation.isValid === false && phoneValidation.requiredWarning !== true) {
        Modal.warning({
          title: phoneValidation.title,
          content: phoneValidation.message,
          okText: "OK",
        });
        return;
      }

      if (phoneValidation.isValid === false && phoneValidation.requiredWarning === true) {
        form.setFieldsValue({ phone: phoneValidation.local_phone });
        form.validateFields(["phone"]);
        return;
      }

      actionHandler("identification", values);

      // Error handling
    } catch (error) {
      // console.log("onFinish Error", error);
    }
  };

  const updateCountryAndRegion = (country_code, region_code) => {
    actionHandler("geo", { country_code, region_code });
  };

  const onFinishFailed = (errorInfo) => {
    setValidationTrigger("onChange");

    console.log("Failed:", errorInfo);
  };

  // const countriesMenu = (
  //   <Menu
  //     items={getCountriesList()}
  //     onClick={(e) => setSelectedCountry(countries.find((el) => el.value === e.key))}
  //   />
  // );
  // const regionsMenu = (
  //   <Menu
  //     items={getRegionsList()}
  //     onClick={(e) => setSelectedRegion(selectedCountry.regions.find((el) => el.value === e.key))}
  //   />
  // );

  // const getSelectedCountryName = (selCountry) => {
  //   try {
  //     console.log("selCountry", selCountry);
  //     if (selCountry === undefined || selCountry === null) return "Portugal";
  //     const selectedCountry = countries.find((country) => country.value === selCountry);
  //     return selectedCountry.localName;
  //   } catch (error) {
  //     // setSelectedCountry("pt");
  //   }
  // };

  // const getSelectedRegionName = (selRegion) => {
  //   if (!country) return "pt";
  //   try {
  //     const selectedRegion = countries.regions.find((region) => region.value === selRegion);
  //     return selectedRegion.name;
  //   } catch (error) {
  //     setSelectedRegion("pt");
  //   }
  // };

  const onChangeEmail = (event, { newValue }) => {
    setInputEmail(newValue.replace(/\s/g, ""));
  };

  const emailSpellCheckerInfo = (email) => {
    if (!email) return;
    try {
      const suggestedEmail = emailSpellChecker.run({
        domains: POPULAR_DOMAINS,
        topLevelDomains: POPULAR_TLDS,
        email,
      });

      if (suggestedEmail) {
        setEmailSpellSuggestion(suggestedEmail.full.trim().toLowerCase());
      } else {
        setEmailSpellSuggestion(null);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const copyFromIdentificationToBilling = () => {
    const nameValue = form.getFieldValue("name");
    const emailValue = form.getFieldValue("email");
    form.setFieldsValue({ billing_name: nameValue, billing_email: emailValue });
    onUpdateFields();
  };

  const showBillingContainer = (data?.format === "one-time-service" || data?.required_checkout_billing === true) && data?.is_teaser !== true;

  const requiredVatId = data?.required_checkout_vat_id === true;
  const requiredCompleteBilling = data?.required_checkout_complete_billing === true;

  return (
    <div className={`identification-container-${customFieldSize} ${data.next_step === "select-payment-method" ? "identification-container-animation" : ""}`}>
      <Form
        form={form}
        scrollToFirstError={{
          behavior: "smooth",
          block: "start",
          inline: "start",
          scrollMode: "always",
        }}
        name="frm-identification"
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onBlurCapture={(e) => {
          try {
            saveOnLocalStorage("identification", form.getFieldsValue());
          } catch (error) {
            console.log(error);
          }
        }}
        autoComplete="off"
      >
        <Row gutter={isSmallCustomFieldSize ? 10 : 0}>
          <Col xs={24} sm={isSmallCustomFieldSize ? 12 : 24}>
            <Form.Item
              label={data?.format === "one-time-service" ? t("identification.nameLabelService") : t("identification.nameLabel")}
              name="name"
              hasFeedback
              rules={[
                { required: true, message: t("identification.required") },
                {
                  validator: (rule, value) => {
                    if (!value) return Promise.resolve();
                    if (value.length === 0) Promise.resolve();

                    if (value?.length < 3) {
                      return Promise.reject(t("identification.nameTooShort"));
                    }

                    if (value?.length > 80) {
                      return Promise.reject(t("identification.nameTooLong"));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              validateTrigger={validationTrigger}
            >
              {/* <Input size="large" placeholder="O teu nome (no minimo primeiro e último nome)" /> */}
              <Input size="large" placeholder={t("identification.namePlaceholder")} onChange={(e) => onUpdateFields(e.target.value)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={isSmallCustomFieldSize ? 12 : 24}>
            <Form.Item
              className={emailSpellSuggestion ? "mb-5" : null}
              label={t("identification.emailLabel")}
              name="email"
              normalize={(value) => (value ? value.replace(/[^\x20-\x7E]/g, "").trim() : value)}
              rules={[
                {
                  required: true,
                  message: t("identification.required"),
                },
                { type: "email", message: t("identification.emailInvalid") },
              ]}
              validateTrigger={validationTrigger}
              hasFeedback
            >
              <Input
                size="large"
                placeholder={t("identification.emailPlaceholder")}
                value={inputEmail}
                onChange={(e) => {
                  let _email = e.target.value.replace(/[^\x20-\x7E]/g, "").trim();

                  // Remove any kind of accents
                  _email = _email.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

                  setLoadingNextButton(true);
                  setInputEmail(_email);
                  onUpdateFields();
                }}
              />
            </Form.Item>
            {emailSpellSuggestion && (
              <div style={{ marginTop: 6 }}>
                <Alert
                  message={
                    <>
                      {t("identification.didYouMean")}{" "}
                      <CustomButton
                        buttonStyle={customStyle}
                        type="link"
                        className="m-0 p-0"
                        onClick={() => {
                          form.setFieldsValue({ email: emailSpellSuggestion });
                          setLoadingNextButton(true);
                          setInputEmail(emailSpellSuggestion);
                          //setEmailSpellSuggestion(null);
                        }}
                      >
                        {emailSpellSuggestion}
                      </CustomButton>
                      ?
                    </>
                  }
                  type="error"
                  showIcon
                  style={{ marginBottom: 10 }}
                />
              </div>
            )}
          </Col>
        </Row>

        <Row gutter={isSmallCustomFieldSize ? 10 : 0}>
          <Col xs={24} sm={isSmallCustomFieldSize ? 12 : 24}>
            <Form.Item noStyle>
              <Form.Item
                className={phoneNumberData?.error === true ? "mb-0" : "mb-1"}
                label={t("identification.phoneLabel")}
                name="phone"
                rules={[
                  {
                    required: data?.customization?.phone_required === true,
                    message: t("identification.required"),
                  },
                ]}
                hasFeedback
                // a stored phone of null would reach PhoneInput and break on value.startsWith()
                getValueProps={(value) => ({ value: value ?? "" })}
              >
                <PhoneInput
                  className="internacional-phone-input"
                  value={phoneNumberData?.phone}
                  defaultCountry={phoneCountriesAllowed.includes(country?.key) === true ? country.key : "pt"}
                  placeholder={t("identification.phonePlaceholder")}
                  countries={allowedCountriesList}
                  preferredCountries={["pt", "es", "fr", "ie", "gb"]}
                  disableDialCodeAndPrefix={true}
                  onChange={(phone, meta) => {
                    setPhoneNumberData({ phone, data: meta, error: false });
                    onUpdateFields();
                    setLoadingNextButton(true);
                    setTimeout(() => {
                      setLoadingNextButton(true);
                    }, 1000);
                  }}
                />
              </Form.Item>
              {phoneNumberData?.error === true && (
                <Alert message={t("identification.phoneInvalid")} type="error" showIcon style={{ marginTop: 10, marginBottom: 15 }} />
              )}
            </Form.Item>
          </Col>

          {/* Ask VAT ID during indentification */}
          {data?.ask_vatid_on_checkout === true && data?.required_checkout_billing !== true && (
            <Col xs={24} sm={isSmallCustomFieldSize ? 12 : 24}>
              <Form.Item
                label={t("identification.vatLabel")}
                name="vatid"
                rules={[
                  {
                    required: data?.customization?.vatid_on_checkout_required === true || data?.required_checkout_vat_id === true,
                    message: t("identification.vatRequired"),
                  },
                  {
                    validator: async (_, value) => {
                      // If Country isnt Portugal, then dont validate VATID

                      if (country?.value?.toUpperCase() !== "PT") return Promise.resolve();

                      if (value === undefined || value === "" || value === null) {
                        return Promise.resolve();
                      }

                      if (value.length > 0 && value.length < 9 && isNaN(value) === false) {
                        return Promise.reject(t("identification.vatIncomplete"));
                      }
                      if (value.length > 0 && value.length < 9 && isNaN(value) === true) {
                        return Promise.reject(t("identification.vatInvalid"));
                      }

                      const rv = await validateVatId(value, true);
                      if (rv.status) {
                        if (rv.data.status === false) {
                          return Promise.reject(t(rv.data.message));
                        } else {
                          return Promise.resolve(t("identification.vatValid"));
                        }
                      } else {
                        return Promise.reject(t("identification.vatVerifyFailed"));
                      }
                    },
                  },
                  {
                    required: false,
                  },
                ]}
                hasFeedback
                validateTrigger={validationTrigger}
              >
                <Input
                  size="large"
                  onChange={(phone) => onUpdateFields()}
                  //placeholder="O teu número de contribuinte (Opcional)"
                  placeholder={`${t("identification.vatLabel")} ${data?.customization?.vatid_on_checkout_required !== true && data?.required_checkout_vat_id !== true ? t("identification.vatOptional") : ""}`}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        {data?.allow_coupons?.active === true && data?.allow_coupons?.show_on_identification === true && (
          <Form.Item label={t("identification.couponLabel")}>
            <Coupon show data={data} actionHandler={actionHandler} customStyle={customStyle} />
          </Form.Item>
        )}

        {data?.free_resolution_waiver_enabled === true && data?.free_resolution_waiver_mandatory === true && (
          <div className="free-resolution-card">
            <Form.Item
              name="free_resolution_checkbox"
              className="mb-0 pb-0"
              valuePropName="checked"
              rules={[
                {
                  validator: (rule, value) => {
                    if (!value) return Promise.reject(t("identification.freeResolutionWarning"));
                    if (value === true) return Promise.resolve();

                    return Promise.reject(t("identification.freeResolutionWarning"));
                  },
                },
              ]}
            >
              <Checkbox>{t("identification.freeResolutionCheckbox")}</Checkbox>
            </Form.Item>
            <div className="free-resolution-notice">
              <small>
                {t("identification.freeResolutionNoticePre")} <strong>{t("identification.freeResolutionNoticeStrong")}</strong>{" "}
                {t("identification.freeResolutionNoticePost")}
              </small>
            </div>
          </div>
        )}

        {data?.allow_offer === true && data?.is_teaser !== true && (
          <>
            <Form.Item name="is_offer" className="mb-0 pb-0" valuePropName="checked">
              <Checkbox onChange={(e) => setIsOfferPurchase(e.target.checked)}>{t("identification.offerCheckbox")}</Checkbox>
            </Form.Item>
            <div>
              <small>{t("identification.offerNotice")}</small>
            </div>
            {isOfferPurchase === true && <GiftSection form={form} />}
          </>
        )}

        {/* ***********************************************************************************
          FORMAT IS ONE TIME SERVICE OR REQUIRED CHECKOUT BILLING IS TRUE 
          *********************************************************************************** */}
        {showBillingContainer === true && (
          <div className="identification-container-small identification-billing-container">
            <div className="billing-card">
              <div className="billing-card__head">
                <span className="billing-card__title">
                  <span className="billing-card__icon" aria-hidden="true">
                    <FileTextOutlined />
                  </span>
                  {t("identification.billingTitle")}
                </span>
                <CustomButton type="link" buttonStyle={customStyle} addClass="billing-card__copy" onClick={copyFromIdentificationToBilling}>
                  <CopyOutlined /> {t("identification.copyBillingData")}
                </CustomButton>
              </div>

              <div className="billing-card__body">
                <VatId country={data?.country} form={form} required={requiredVatId === true || requiredCompleteBilling === true} />
                <BillingName />
                <BillingEmail />
                <Address required={requiredCompleteBilling === true} />
                <City required={requiredCompleteBilling === true} />
                <PostalCode required={requiredCompleteBilling === true} selectedCountry={country?.value} />
              </div>
            </div>
          </div>
        )}
        {isMobileSize && (
          <>
            <Form.Item name="country-region">
              <div className="country-mobile-inline">
                <label style={{ marginRight: 5, marginBottom: 0 }}>{t("identification.countryLabel")}</label>
                <MobileGeoLocationSelector
                  countries={countries}
                  selectedCountry={country}
                  selectedRegion={region}
                  onChange={(country, region) => updateCountryAndRegion(country, region)}
                  customStyle={customStyle}
                  modalTitle={t("identification.countryPickerModalTitle")}
                  searchPlaceholder={t("identification.countryPickerSearchPlaceholder")}
                  closeText={t("identification.close")}
                  noResultsText={t("identification.countryPickerNoResults")}
                />
              </div>
            </Form.Item>
            <Form.Item>
              <CustomButton
                buttonStyle={customStyle}
                customRef={nextButtonRef}
                block
                //disabled={loadingNextButton}
                size="large"
                type="primary"
                htmlType="submit"
                // className="submit-button"
              >
                {t("identification.nextStep")} <RightOutlined />
              </CustomButton>
            </Form.Item>
          </>
        )}
        {!isMobileSize && (
          <div style={{ marginTop: 15, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Form.Item name="country-region">
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <label style={{ marginRight: 5 }}>{t("identification.countryLabel")}</label>
                <MobileGeoLocationSelector
                  countries={countries}
                  selectedCountry={country}
                  selectedRegion={region}
                  onChange={(country, region) => updateCountryAndRegion(country, region)}
                  customStyle={customStyle}
                  modalTitle={t("identification.countryPickerModalTitle")}
                  searchPlaceholder={t("identification.countryPickerSearchPlaceholder")}
                  closeText={t("identification.close")}
                  noResultsText={t("identification.countryPickerNoResults")}
                />
              </div>
            </Form.Item>

            <Form.Item>
              <CustomButton
                buttonStyle={customStyle}
                customRef={nextButtonRef}
                size="large"
                type="primary"
                htmlType="submit"
                //  className="submit-button"
              >
                {t("identification.nextStep")} <RightOutlined />
              </CustomButton>
            </Form.Item>
          </div>
        )}
        {nextButtonInView === false && (
          <div className="nextButtonFixed">
            <Form.Item noStyle>
              <CustomButton buttonStyle={customStyle} type="primary" htmlType="submit" addClass="submit-button-smaller">
                {t("identification.nextStep")} <RightOutlined />
              </CustomButton>
            </Form.Item>
          </div>
        )}
      </Form>
      {data?.is_teaser !== true && data?.format !== "one-time-service" && showBillingContainer !== true && (
        <div className="billing-data-notice">
          <BuyerInvoiceInfo isWidget={isWidget} data={data?.thankyou_page} />
        </div>
      )}
    </div>
  );
};

export default Identification;
